import type { FinancialEntryItem, FinancialFieldDefinition } from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { T } from "./entry-list.i18n";
import { RequiredTypeLabel } from "./required-type-label";
import { getFieldLabel } from "./translation-utils";
import { formatNumberInput, MAX_ITEMS_PER_CATEGORY, parseNumberInput } from "./utils";

type EntryListProps = {
  entries: FinancialEntryItem[];
  fieldDefs: FinancialFieldDefinition[];
  /** The tab's built-in labels, already picked for the language (`t.fieldLabels`). */
  fieldLabels: Readonly<Record<string, string>>;
  /** QL-39 — ids of the required fields; their label gets a * and their last row cannot be removed. */
  requiredTypeIds?: ReadonlySet<string>;
  /** QL-39 — required fields still lacking an amount; their amount input is outlined. */
  missingRequiredIds?: ReadonlySet<string>;
  disabled: boolean;
  lang: string;
  showMonthlyPayment?: boolean;
  addButtonLabel: string;
  emptyMessage: string;
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<FinancialEntryItem>) => void;
  hasMonthlyPaymentForType?: (typeId: string) => boolean;
};

export function EntryList({
  entries,
  fieldDefs,
  fieldLabels,
  requiredTypeIds,
  missingRequiredIds,
  disabled,
  lang,
  showMonthlyPayment = false,
  addButtonLabel,
  emptyMessage,
  onAdd,
  onRemove,
  onUpdate,
  hasMonthlyPaymentForType,
}: EntryListProps) {
  const { formatNumber, parseNumber } = useRegionalFormat();
  const t = useT(T);
  const isRequired = (typeId: string) => requiredTypeIds?.has(typeId) === true;
  const isMissing = (typeId: string) => missingRequiredIds?.has(typeId) === true;
  const isLastRowOfRequired = (entry: FinancialEntryItem) =>
    isRequired(entry.typeId) && entries.filter((e) => e.typeId === entry.typeId).length === 1;

  return (
    <div className="space-y-3">
      {/* Header row */}
      {entries.length > 0 && (
        <div
          className={`grid gap-2 text-sm font-medium text-muted-foreground ${
            showMonthlyPayment
              ? "grid-cols-[minmax(80px,300px)_minmax(70px,150px)_minmax(60px,135px)_minmax(120px,255px)_32px]"
              : "grid-cols-[minmax(100px,375px)_minmax(80px,165px)_minmax(130px,270px)_32px]"
          }`}
        >
          <div>{t.description}</div>
          <div>{t.amount}</div>
          {showMonthlyPayment && <div>{t.monthly}</div>}
          <div>{t.type}</div>
          <div />
        </div>
      )}

      {/* Entry rows */}
      {entries.map((entry) => (
        <div
          key={entry.id}
          className={`grid gap-2 items-center ${
            showMonthlyPayment
              ? "grid-cols-[minmax(80px,300px)_minmax(70px,150px)_minmax(60px,135px)_minmax(120px,255px)_32px]"
              : "grid-cols-[minmax(100px,375px)_minmax(80px,165px)_minmax(130px,270px)_32px]"
          }`}
        >
          <Input
            value={entry.label}
            onChange={(e) => onUpdate(entry.id, { label: e.target.value })}
            disabled={disabled}
            placeholder={t.descriptionPlaceholder}
            className="h-8"
          />

          <Input
            type="text"
            inputMode="numeric"
            value={formatNumberInput(entry.amount, formatNumber)}
            onChange={(e) =>
              onUpdate(entry.id, { amount: parseNumberInput(e.target.value, parseNumber) })
            }
            disabled={disabled}
            placeholder={t.amountPlaceholder}
            aria-invalid={isMissing(entry.typeId) || undefined}
            className={cn("h-8 text-right", isMissing(entry.typeId) && "border-destructive")}
          />

          {showMonthlyPayment && (
            <Input
              type="text"
              inputMode="numeric"
              value={formatNumberInput(entry.monthlyPayment, formatNumber)}
              onChange={(e) =>
                onUpdate(entry.id, {
                  monthlyPayment: parseNumberInput(e.target.value, parseNumber),
                })
              }
              disabled={disabled || !hasMonthlyPaymentForType?.(entry.typeId)}
              placeholder={hasMonthlyPaymentForType?.(entry.typeId) ? t.amountPlaceholder : "-"}
              className="h-8 text-right"
            />
          )}

          <Select
            value={entry.typeId}
            onValueChange={(v) => onUpdate(entry.id, { typeId: v })}
            disabled={disabled}
          >
            <SelectTrigger className="h-8">
              <SelectValue>
                <RequiredTypeLabel
                  label={getFieldLabel(entry.typeId, lang, fieldDefs, fieldLabels)}
                  required={isRequired(entry.typeId)}
                />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {fieldDefs.map((field) => (
                <SelectItem key={field.id} value={field.id}>
                  {getFieldLabel(field.id, lang, fieldDefs, fieldLabels)}
                  {isRequired(field.id) && <span className="text-destructive"> *</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            tooltip={t.remove}
            onClick={() => onRemove(entry.id)}
            disabled={disabled || isLastRowOfRequired(entry)}
            className="h-8 w-8"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
          </Button>
        </div>
      ))}

      {/* Add button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onAdd}
        disabled={disabled || entries.length >= MAX_ITEMS_PER_CATEGORY || fieldDefs.length === 0}
        className="w-full"
      >
        {addButtonLabel}
      </Button>

      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">{emptyMessage}</p>
      )}
    </div>
  );
}
